import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { format, subDays, startOfMonth } from "date-fns";
import {
  CalendarIcon, Loader2, TrendingUp, Users, Clock, DollarSign,
  Activity, UserCheck, Repeat, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell,
} from "recharts";
import { getAnalyticsDashboard } from "@/lib/api";

const C = ["#0B4F63", "#C86F53", "#D49A42", "#5C7A85", "#4A6C8C", "#8FA98F"];
const AXIS_TICK = { fill: "#5C7A85", fontSize: 12 };
const TOOLTIP_STYLE = { backgroundColor: "#fff", border: "1px solid #E5E2DC", borderRadius: "8px" };
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const PRESETS = [
  { key: "7d", label: "7 Days", get: () => [subDays(new Date(), 6), new Date()] },
  { key: "30d", label: "30 Days", get: () => [subDays(new Date(), 29), new Date()] },
  { key: "month", label: "This Month", get: () => [startOfMonth(new Date()), new Date()] },
  { key: "90d", label: "90 Days", get: () => [subDays(new Date(), 89), new Date()] },
];

export default function Analytics() {
  const { selectedBranch } = useOutletContext();
  const branchId = selectedBranch?.id;
  const [range, setRange] = useState(() => PRESETS[1].get());
  const [activePreset, setActivePreset] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [start, end] = range;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start_date: format(start, "yyyy-MM-dd"), end_date: format(end, "yyyy-MM-dd") };
      if (branchId) params.branch_id = branchId;
      setData(await getAnalyticsDashboard(params));
    } catch {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [start, end, branchId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const applyPreset = (p) => { setActivePreset(p.key); setRange(p.get()); };

  return (
    <div className="space-y-6 animate-in" data-testid="analytics-page">
      <div className="flex flex-col lg:flex-row gap-4 justify-between lg:items-center">
        <div>
          <h2 className="font-heading text-2xl font-bold text-forest flex items-center gap-2">
            <TrendingUp className="h-6 w-6" /> Business Intelligence
          </h2>
          <p className="text-sage text-sm">4-axis performance dashboard — {selectedBranch?.name || "All branches"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {PRESETS.map((p) => (
            <Button key={p.key} size="sm" variant={activePreset === p.key ? "default" : "outline"}
              className={activePreset === p.key ? "bg-forest hover:bg-forest-light" : ""}
              onClick={() => applyPreset(p)} data-testid={`preset-${p.key}`}>
              {p.label}
            </Button>
          ))}
          <DateRangePicker start={start} end={end} onChange={(r) => { setActivePreset("custom"); setRange(r); }} />
        </div>
      </div>

      {loading || !data ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-forest" /></div>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi icon={Activity} label="Total Sessions" value={data.totals.sessions} testid="kpi-sessions" />
            <Kpi icon={DollarSign} label="Total Revenue" value={money(data.totals.revenue)} testid="kpi-revenue" />
            <Kpi icon={Users} label="Unique Clients" value={data.totals.unique_clients} testid="kpi-clients" />
            <Kpi icon={Sparkles} label="Avg Spend Per Head" value={money(data.totals.avg_ticket)} testid="kpi-ticket" highlight />
          </div>

          <Tabs defaultValue="time" className="space-y-5">
            <TabsList className="grid grid-cols-2 sm:grid-cols-4 w-full">
              <TabsTrigger value="time" data-testid="axis-time"><Clock className="h-4 w-4 mr-1.5" /> Time</TabsTrigger>
              <TabsTrigger value="customer" data-testid="axis-customer"><UserCheck className="h-4 w-4 mr-1.5" /> Customer</TabsTrigger>
              <TabsTrigger value="therapist" data-testid="axis-therapist"><Users className="h-4 w-4 mr-1.5" /> Therapist</TabsTrigger>
              <TabsTrigger value="finance" data-testid="axis-finance"><DollarSign className="h-4 w-4 mr-1.5" /> Finance</TabsTrigger>
            </TabsList>

            {/* AXIS 1 — TIME & CAPACITY */}
            <TabsContent value="time" className="space-y-5">
              <Panel title="Occupancy Heatmap" subtitle="Session density by day & hour — spot peak rushes and dead zones">
                <Heatmap heatmap={data.occupancy_heatmap} />
              </Panel>
              <Panel title="Booking Duration Mix" subtitle="Client duration preferences for shift planning">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.duration_distribution}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DC" />
                      <XAxis dataKey="duration" tick={AXIS_TICK} tickFormatter={(v) => `${v}m`} />
                      <YAxis tick={AXIS_TICK} />
                      <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v, n, p) => [`${v} (${p.payload.pct}%)`, "Sessions"]} />
                      <Bar dataKey="count" fill="#0B4F63" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Panel>
            </TabsContent>

            {/* AXIS 2 — CUSTOMER */}
            <TabsContent value="customer" className="space-y-5">
              <div className="grid lg:grid-cols-2 gap-5">
                <Panel title="Customer Retention" subtitle="New vs returning clients">
                  <div className="flex items-center gap-6">
                    <div className="h-48 w-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={[{ name: "Returning", value: data.customer_retention.returning }, { name: "New", value: data.customer_retention.new }]}
                            dataKey="value" innerRadius={45} outerRadius={75} paddingAngle={3}>
                            <Cell fill="#0B4F63" /><Cell fill="#D49A42" />
                          </Pie>
                          <Tooltip contentStyle={TOOLTIP_STYLE} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-3">
                      <StatLine icon={Repeat} color="text-forest" label="Returning" value={data.customer_retention.returning} />
                      <StatLine icon={Sparkles} color="text-ochre" label="New" value={data.customer_retention.new} />
                      <div className="pt-2 border-t border-border">
                        <p className="text-xs text-sage">Retention Rate</p>
                        <p className="text-2xl font-bold text-forest" data-testid="retention-rate">{data.customer_retention.returning_rate}%</p>
                      </div>
                    </div>
                  </div>
                </Panel>
                <Panel title="Booking Status" subtitle="Completed booking mix (no-show tracking)">
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={data.status_distribution.map((s) => ({ name: s.status, value: s.count }))}
                          dataKey="value" nameKey="name" outerRadius={75} label>
                          {data.status_distribution.map((s, i) => <Cell key={s.status} fill={C[i % C.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={TOOLTIP_STYLE} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </Panel>
              </div>
              <div className="grid lg:grid-cols-2 gap-5">
                <Panel title="Top Services — Volume" subtitle="Most frequently booked">
                  <HBar items={data.top_services_volume} valueKey="count" color="#0B4F63" fmt={(v) => v} />
                </Panel>
                <Panel title="Top Services — Revenue" subtitle="Highest gross earners">
                  <HBar items={data.top_services_revenue} valueKey="revenue" color="#C86F53" fmt={money} />
                </Panel>
              </div>
            </TabsContent>

            {/* AXIS 3 — THERAPIST */}
            <TabsContent value="therapist" className="space-y-5">
              <Panel title="Operational Shift Hours" subtitle="Accumulated working hours (from attendance logs)">
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.therapist_hours} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DC" />
                      <XAxis type="number" tick={AXIS_TICK} />
                      <YAxis type="category" dataKey="name" tick={AXIS_TICK} width={80} />
                      <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [`${v}h`, "Hours"]} />
                      <Bar dataKey="hours" fill="#0B4F63" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Panel>
              <Panel title="Client Request Rate" subtitle="Share of pre-booked (requested) sessions per therapist">
                {data.therapist_request_rate.length === 0 ? (
                  <p className="text-sm text-sage/70 py-6 text-center">No data for this range.</p>
                ) : (
                  <div className="space-y-3">
                    {data.therapist_request_rate.map((t) => (
                      <div key={t.staff_id} data-testid={`request-rate-${t.staff_id}`}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-forest font-medium">{t.name}</span>
                          <span className="text-sage">{t.booked}/{t.total} · {t.rate}%</span>
                        </div>
                        <div className="h-2.5 bg-sand rounded-full overflow-hidden">
                          <div className="h-full bg-forest rounded-full" style={{ width: `${t.rate}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>
            </TabsContent>

            {/* AXIS 4 — FINANCE */}
            <TabsContent value="finance" className="space-y-5">
              <Panel title="Revenue & Growth Velocity" subtitle="Daily revenue across the selected range">
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.revenue_timeseries}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DC" />
                      <XAxis dataKey="date" tick={AXIS_TICK} tickFormatter={(d) => format(new Date(d), "d MMM")} />
                      <YAxis tick={AXIS_TICK} />
                      <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [money(v), "Revenue"]} />
                      <Line type="monotone" dataKey="revenue" stroke="#0B4F63" strokeWidth={2.5} dot={{ fill: "#0B4F63", r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Panel>
              <div className="grid lg:grid-cols-2 gap-5">
                <Panel title="Payment Method Share" subtitle="Cash vs Eftpos — manage float & banking">
                  <div className="flex items-center gap-6">
                    <div className="h-48 w-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={[{ name: "Cash", value: data.payment_distribution.cash }, { name: "Eftpos", value: data.payment_distribution.eftpos }]}
                            dataKey="value" innerRadius={45} outerRadius={75} paddingAngle={3}>
                            <Cell fill="#0B4F63" /><Cell fill="#C86F53" />
                          </Pie>
                          <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => money(v)} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-sage flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-forest" /> Cash</p>
                        <p className="text-lg font-bold text-forest" data-testid="pay-cash">{money(data.payment_distribution.cash)} · {data.payment_distribution.cash_pct}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-sage flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-terracotta" /> Eftpos</p>
                        <p className="text-lg font-bold text-terracotta" data-testid="pay-eftpos">{money(data.payment_distribution.eftpos)} · {data.payment_distribution.eftpos_pct}%</p>
                      </div>
                    </div>
                  </div>
                </Panel>
                <Panel title="Spend Per Head" subtitle="Average ticket value per unique client visit">
                  <div className="flex flex-col items-center justify-center h-48">
                    <p className="text-5xl font-heading font-bold text-forest">{money(data.totals.avg_ticket)}</p>
                    <p className="text-sage text-sm mt-2">{money(data.totals.revenue)} across {data.totals.unique_clients} clients</p>
                  </div>
                </Panel>
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, testid, highlight }) {
  return (
    <div className={`rounded-xl p-4 border ${highlight ? "bg-forest border-forest" : "bg-white border-border"}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`h-4 w-4 ${highlight ? "text-white/80" : "text-sage"}`} />
        <span className={`text-xs ${highlight ? "text-white/80" : "text-sage"}`}>{label}</span>
      </div>
      <p className={`text-2xl font-bold ${highlight ? "text-white" : "text-forest"}`}>{value}</p>
    </div>
  );
}

function Panel({ title, subtitle, children }) {
  return (
    <div className="bg-white border border-border rounded-xl p-5">
      <h3 className="font-heading font-semibold text-forest">{title}</h3>
      {subtitle && <p className="text-xs text-sage mb-4">{subtitle}</p>}
      {children}
    </div>
  );
}

function StatLine({ icon: Icon, color, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className={`h-4 w-4 ${color}`} />
      <span className="text-sm text-sage">{label}</span>
      <span className="text-lg font-bold text-forest ml-auto">{value}</span>
    </div>
  );
}

function HBar({ items, valueKey, color, fmt }) {
  if (!items || items.length === 0) return <p className="text-sm text-sage/70 py-6 text-center">No data.</p>;
  const max = Math.max(...items.map((i) => i[valueKey]), 1);
  return (
    <div className="space-y-2.5">
      {items.map((i) => (
        <div key={i.name}>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-forest truncate">{i.name}</span>
            <span className="text-sage shrink-0 ml-2">{fmt(i[valueKey])}</span>
          </div>
          <div className="h-2.5 bg-sand rounded-full overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${(i[valueKey] / max) * 100}%`, backgroundColor: color }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function Heatmap({ heatmap }) {
  const hours = heatmap[0]?.cells.map((c) => c.hour) || [];
  const max = Math.max(1, ...heatmap.flatMap((r) => r.cells.map((c) => c.count)));
  const shade = (n) => (n === 0 ? "#F5F3EE" : `rgba(11,79,99,${0.15 + 0.85 * (n / max)})`);
  return (
    <div className="overflow-x-auto" data-testid="occupancy-heatmap">
      <table className="border-separate" style={{ borderSpacing: 3 }}>
        <thead>
          <tr>
            <th className="w-10" />
            {hours.map((h) => <th key={h} className="text-[10px] text-sage font-medium w-9">{h}:00</th>)}
          </tr>
        </thead>
        <tbody>
          {heatmap.map((row) => (
            <tr key={row.day}>
              <td className="text-[11px] text-sage font-medium pr-1">{row.day}</td>
              {row.cells.map((c) => (
                <td key={c.hour}>
                  <div className="h-8 w-9 rounded flex items-center justify-center text-[10px] font-medium"
                    style={{ backgroundColor: shade(c.count), color: c.count > max * 0.55 ? "#fff" : "#5C7A85" }}
                    title={`${row.day} ${c.hour}:00 — ${c.count} sessions`}>
                    {c.count || ""}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DateRangePicker({ start, end, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid="custom-range-btn">
          <CalendarIcon className="h-4 w-4 mr-2" />
          {format(start, "d MMM")} – {format(end, "d MMM")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <Calendar mode="range" selected={{ from: start, to: end }}
          onSelect={(r) => { if (r?.from && r?.to) { onChange([r.from, r.to]); setOpen(false); } }}
          numberOfMonths={2} />
      </PopoverContent>
    </Popover>
  );
}
